import { useEffect, useState } from "react";
import client, { apiErr } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Plus, ClipboardList, Loader2, MapPin, XCircle, Trash2, Search, Copy } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { BatchBar } from "@/components/BatchBar";
import { toast } from "sonner";
import { FIELD_JOB_TYPES, JOB_TYPE_LABELS, jobTypeLabel } from "@/lib/fieldTypes";
import { WorkerSelect } from "@/components/WorkerSelect";
import { hasField, hasConnect } from "@/lib/mergeStaff";
import { useNavigate } from "react-router-dom";
import { usePoll } from "@/hooks/usePoll";
import { confirm } from "@/components/ConfirmDialog";
import { SearchSelect } from "@/components/SearchSelect";
import { JobNumberLink } from "@/components/JobNumberLink";
import { MapLink } from "@/components/MapLink";

const STAT = { scheduled: "bg-emerald-50 text-emerald-700 border-emerald-200", active: "bg-amber-50 text-amber-700 border-amber-200", in_progress: "bg-amber-50 text-amber-700 border-amber-200", completed: "bg-slate-100 text-slate-600", cancelled: "bg-red-50 text-red-700 border-red-200" };
const EMPTY = { clientId: "", propertyId: "", workerIds: [], salesmanId: "", addressDetails: "", date: "", startTime: "09:00", endTime: "17:00", jobType: "one_off", jobTypeOther: "", instructions: "", accessInstructions: "" };

export default function Jobs() {
  const { t } = useI18n();
  const { can, user } = useAuth();
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [clients, setClients] = useState([]);
  const [properties, setProperties] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [jobTypes, setJobTypes] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState({});
  const [dup, setDup] = useState(null);
  const [dupDate, setDupDate] = useState("");

  const selIds = Object.keys(sel).filter((k) => sel[k]);
  const batch = async (action) => {
    if (!selIds.length) return;
    if (!(await confirm({ title: action === "delete" ? "Delete jobs" : "Cancel jobs", description: `${action === "delete" ? "Permanently delete" : "Cancel"} ${selIds.length} job(s)?`, confirmText: action === "delete" ? "Delete" : "Cancel jobs", destructive: true }))) return;
    try {
      const { data } = await client.post("/jobs/batch", { ids: selIds, action });
      const reasons = [...new Set((data.skipped || []).map((s) => s.reason).filter(Boolean))].join("; ");
      if (data.done.length) toast.success(`${data.done.length} job(s) ${action === "delete" ? "deleted" : "cancelled"}${data.skipped.length ? ` · ${data.skipped.length} skipped (${reasons})` : ""}.`);
      else toast.warning(`Nothing ${action === "delete" ? "deleted" : "cancelled"} — ${data.skipped.length} skipped${reasons ? `: ${reasons}` : ""}.`);
      setSel({}); load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const load = () => client.get("/jobs").then((r) => setRows(r.data)).catch(() => {});

  const doDuplicate = async () => {
    if (!dup || !dupDate) return;
    setBusy(true);
    try {
      await client.post(`/calendar/job/${dup.id}/duplicate`, { date: dupDate });
      toast.success(t("job.duplicated", "Job duplicated"));
      setDup(null); setDupDate(""); load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  useEffect(() => {
    load();
    client.get("/clients").then((r) => setClients(r.data)).catch(() => {});
    client.get("/properties").then((r) => setProperties(r.data)).catch(() => {});
    client.get("/job-types").then((r) => setJobTypes(r.data || [])).catch(() => {});
    client.get("/users").then((r) => {
      setWorkers(r.data.filter((u) => (u.role === "cleaner" || u.role === "supervisor") && u.status !== "deactivated"));
      setAdmins(r.data.filter((u) => ["owner", "admin", "office"].includes(u.role) && u.status !== "deactivated"));
    }).catch(() => {});
  }, []);
  usePoll(load, 15000);

  const fieldWorkers = workers.filter((w) => hasField(w.operationalAccess || "CONNECT_ONLY"));
  const connectWorkers = workers.filter((w) => hasConnect(w.operationalAccess || "CONNECT_ONLY"));
  const jtOptions = jobTypes.length ? jobTypes : FIELD_JOB_TYPES.map((k) => ({ value: k, label: JOB_TYPE_LABELS[k] || k }));
  const jtLabel = (v) => (jobTypes.find((j) => j.value === v) || {}).label || jobTypeLabel(t, v);

  const propsForClient = properties.filter((p) => !form.clientId || p.clientId === form.clientId);
  const filtered = rows
    .filter((j) => {
      const s = q.trim().toLowerCase();
      if (!s) return true;
      if (/^#\d+$/.test(s)) return String(j.jobNumber) === s.slice(1);
      if (/^\d+$/.test(s) && String(j.jobNumber) === s) return true;
      return [j.clientName, j.propertyAddress, j.jobType, j.date].filter(Boolean).some((v) => String(v).toLowerCase().includes(s));
    })
    .slice()
    .sort((a, b) => (a.clientName || "").localeCompare(b.clientName || "") || (a.propertyAddress || "").localeCompare(b.propertyAddress || ""));
  // One row per job number (repeats share it); open the next upcoming visit, else the latest.
  const today = new Date().toLocaleDateString("en-CA", { timeZone: "Europe/London" });
  const groups = (() => {
    const m = new Map();
    filtered.forEach((j) => { const k = j.jobNumber ?? j.id; if (!m.has(k)) m.set(k, []); m.get(k).push(j); });
    return [...m.values()].map((vs) => {
      vs.sort((a, b) => (a.date || "").localeCompare(b.date || ""));
      const up = vs.filter((v) => (v.date || "") >= today && v.status !== "cancelled");
      return { ...(up[0] || vs[vs.length - 1]), groupIds: vs.map((v) => v.id), visitCount: vs.length, upcomingCount: up.length };
    });
  })();
  const allSelected = filtered.length > 0 && filtered.every((j) => sel[j.id]);
  const toggleAll = (v) => { const next = { ...sel }; filtered.forEach((j) => { next[j.id] = !!v; }); setSel(next); };

  const create = async () => {
    setBusy(true);
    try {
      await client.post("/jobs", { ...form, salesmanId: form.salesmanId || user?.id });
      toast.success(t("toast.jobSaved", "Job saved"));
      setOpen(false); setForm(EMPTY); load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const createClientInline = async (name) => {
    const { data } = await client.post("/clients", { name, contact: "" });
    const list = (await client.get("/clients")).data; setClients(list);
    const id = data?.id || (list.find((c) => c.name === name) || {}).id;
    setForm((f) => ({ ...f, clientId: id, propertyId: "" }));
    toast.success(t("toast.clientSaved", "Client saved"));
  };

  const createPropertyInline = async (address) => {
    const payload = { clientId: form.clientId, address, geofenceRadius: 100, accessInfo: "", geo: { lat: null, lng: null } };
    const { data } = await client.post("/properties", payload);
    const list = (await client.get("/properties")).data; setProperties(list);
    const id = data?.id || (list.find((p) => p.clientId === form.clientId && p.address === address) || {}).id;
    setForm((f) => ({ ...f, propertyId: id }));
    toast.success(t("toast.propertySaved", "Property saved"));
  };

  const createPropertyFromAddress = async (r) => {
    const payload = { clientId: form.clientId, address: r.address, geofenceRadius: 100, accessInfo: "", geo: { lat: r.lat ?? null, lng: r.lng ?? null } };
    const { data } = await client.post("/properties", payload);
    const list = (await client.get("/properties")).data; setProperties(list);
    const id = data?.id || (list.find((p) => p.clientId === form.clientId && p.address === r.address) || {}).id;
    setForm((f) => ({ ...f, propertyId: id }));
    toast.success(t("toast.propertySaved", "Property saved"));
  };

  return (
    <div className="space-y-6" data-testid="jobs-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="inline-flex items-center gap-1.5 mb-1">
            <Badge className="bg-brand/10 text-brand border-brand/20" data-testid="field-tag">{t("field.label", "iClean Field")}</Badge>
          </div>
          <h2 className="font-display text-2xl font-bold text-brand-ink">{t("field.label", "iClean Field")} — {t("nav.jobs", "Jobs")}</h2>
          <p className="text-sm text-slate-500">{t("job.fieldSubtitle", "iClean Field — individual field cleaning jobs.")}</p>
        </div>
        {can("job.manage") && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="job-create-button" className="bg-brand hover:bg-brand-dark"><Plus className="h-4 w-4 mr-2" />{t("job.new", "New job")}</Button></DialogTrigger>
            <DialogContent data-testid="job-create-dialog" className="max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle>{t("field.label", "iClean Field")} — {t("job.new", "New job")}</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>{t("job.client", "Client")}</Label>
                  <SearchSelect
                    testid="job-client-select"
                    value={form.clientId}
                    onChange={(v) => setForm({ ...form, clientId: v, propertyId: "" })}
                    options={clients.map((c) => ({ value: c.id, label: c.name }))}
                    placeholder={t("job.client", "Client")}
                    searchPlaceholder={t("job.searchClient", "Search or add a client…")}
                    onCreate={createClientInline}
                    createLabel={t("job.addClient", "Create client")}
                    className="mt-1.5"
                  />
                </div>
                <div>
                  <Label>{t("job.addressDetails", "Extra address details (flat, unit, floor)")}</Label>
                  <Input data-testid="job-address-details" value={form.addressDetails} onChange={(e) => setForm({ ...form, addressDetails: e.target.value })}
                    placeholder={t("job.addressDetailsPlaceholder", "e.g. Flat 3, 2nd floor, buzzer 12")} className="mt-1.5" />
                </div>
                <div>
                  <Label>{t("job.property", "Property")}</Label>
                  <SearchSelect
                    testid="job-property-select"
                    value={form.propertyId}
                    onChange={(v) => setForm({ ...form, propertyId: v })}
                    options={propsForClient.map((p) => ({ value: p.id, label: p.address }))}
                    placeholder={form.clientId ? t("job.property", "Property") : t("job.selectClientFirst", "Select a client first")}
                    searchPlaceholder={t("job.searchProperty", "Search or add an address…")}
                    onCreate={form.clientId ? createPropertyInline : undefined}
                    onCreateFromAddress={form.clientId ? createPropertyFromAddress : undefined}
                    addressSuggest={!!form.clientId}
                    createLabel={t("job.addProperty", "Create property")}
                    disabled={!form.clientId}
                    className="mt-1.5"
                  />
                </div>
                <div>
                  <Label>{t("job.type", "Job type")}</Label>
                  <Select value={form.jobType} onValueChange={(v) => setForm({ ...form, jobType: v })}>
                    <SelectTrigger data-testid="job-type-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                    <SelectContent>{jtOptions.map((jt) => <SelectItem key={jt.value} value={jt.value}>{jt.label}</SelectItem>)}</SelectContent>
                  </Select>
                  {form.jobType === "other" && (
                    <Input data-testid="job-type-other" value={form.jobTypeOther} onChange={(e) => setForm({ ...form, jobTypeOther: e.target.value })} placeholder={t("job.typeOther", "Describe (other)")} className="mt-2" />
                  )}
                </div>
                <div>
                  <Label>{t("job.fieldWorkers", "Field workers")}</Label>
                  <WorkerSelect workers={fieldWorkers} value={form.workerIds} onChange={(v) => setForm({ ...form, workerIds: v })} testid="job-field-workers" placeholder={t("job.selectWorkers", "Select workers…")} />
                </div>
                <div>
                  <Label>{t("job.connectWorkers", "Connect workers")}</Label>
                  <WorkerSelect workers={connectWorkers} value={form.workerIds} onChange={(v) => setForm({ ...form, workerIds: v })} testid="job-connect-workers" placeholder={t("job.selectWorkers", "Select workers…")} />
                  <p className="text-[11px] text-slate-400 mt-1">{t("job.multiHint", "Assign one or more workers. Each records their own time.")}</p>
                </div>
                <div>
                  <Label>{t("job.salesman", "Salesman (added by)")}</Label>
                  <Select value={form.salesmanId || user?.id || ""} onValueChange={(v) => setForm({ ...form, salesmanId: v })}>
                    <SelectTrigger data-testid="job-salesman-select" className="mt-1.5"><SelectValue placeholder={t("job.selectSalesman", "Select salesman")} /></SelectTrigger>
                    <SelectContent>{admins.map((a) => <SelectItem key={a.id} value={a.id}>{a.displayName}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div><Label>{t("job.date", "Date")}</Label><Input type="date" data-testid="job-date-input" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1.5" /></div>
                  <div><Label>{t("sched.start", "Start")}</Label><Input type="time" value={form.startTime} onChange={(e) => setForm({ ...form, startTime: e.target.value })} className="mt-1.5" /></div>
                  <div><Label>{t("sched.end", "End")}</Label><Input type="time" value={form.endTime} onChange={(e) => setForm({ ...form, endTime: e.target.value })} className="mt-1.5" /></div>
                </div>
                <div><Label>{t("job.accessInstructions", "Job access instructions")}</Label><Textarea value={form.accessInstructions} onChange={(e) => setForm({ ...form, accessInstructions: e.target.value })} className="mt-1.5" /></div>
                <div><Label>{t("prop.instructions", "Instructions")}</Label><Textarea value={form.instructions} onChange={(e) => setForm({ ...form, instructions: e.target.value })} className="mt-1.5" /></div>
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setOpen(false)}>{t("common.cancel", "Cancel")}</Button>
                <Button data-testid="job-save-btn" onClick={create} disabled={busy || !form.clientId || !form.propertyId || !form.date} className="bg-brand hover:bg-brand-dark">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : t("common.create", "Create")}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative max-w-md flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input data-testid="jobs-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("job.search", "Search jobs by client, property or date…")} className="pl-9" />
        </div>
      </div>
      <Card className="border-slate-200">
        {can("job.manage") && selIds.length > 0 && (
          <div className="p-3 border-b border-slate-100">
            <BatchBar count={selIds.length} onClear={() => setSel({})} actions={[
              { key: "cancel", label: "Cancel", icon: <XCircle className="h-4 w-4 mr-1.5" />, className: "text-amber-600 border-amber-200", onClick: () => batch("cancel") },
              { key: "delete", label: "Delete", icon: <Trash2 className="h-4 w-4 mr-1.5" />, className: "text-red-600 border-red-200", onClick: () => batch("delete") },
            ]} />
          </div>
        )}
        <Table>
          <TableHeader><TableRow>
            {can("job.manage") && <TableHead className="w-8"><Checkbox data-testid="jobs-select-all" checked={allSelected} onCheckedChange={toggleAll} /></TableHead>}
            <TableHead>{t("job.date", "Date")}</TableHead><TableHead>{t("job.type", "Job type")}</TableHead><TableHead>{t("job.client", "Client")}</TableHead><TableHead>{t("job.property", "Property")}</TableHead>
            <TableHead>{t("job.workers", "Workers")}</TableHead><TableHead>{t("att.time", "Time")}</TableHead><TableHead>{t("common.status", "Status")}</TableHead>
            {can("job.manage") && <TableHead className="w-10 text-right">{t("common.actions", "")}</TableHead>}
          </TableRow></TableHeader>
          <TableBody>
            {groups.length === 0 ? <TableRow><TableCell colSpan={8} className="text-center text-slate-400 py-8"><ClipboardList className="h-7 w-7 mx-auto mb-1" />—</TableCell></TableRow> :
              groups.map((j) => (
                <TableRow key={j.id} data-testid={`job-row-${j.id}`} className="hover:bg-slate-50 cursor-pointer" onClick={() => nav(`/jobs/${j.id}`)}>
                  {can("job.manage") && (
                    <TableCell className="py-2" onClick={(e) => e.stopPropagation()}>
                      <Checkbox data-testid={`job-select-${j.id}`} checked={j.groupIds.every((i) => sel[i])} onCheckedChange={(c) => setSel((s) => { const n = { ...s }; j.groupIds.forEach((i) => { n[i] = !!c; }); return n; })} />
                    </TableCell>
                  )}
                  <TableCell className="font-medium"><span className="mr-2 text-xs" data-testid={`job-number-${j.id}`}><JobNumberLink n={j.jobNumber} testid={`job-number-link-row-${j.id}`} /></span>{j.date}
                    {j.visitCount > 1 && <span className="block text-[11px] font-normal text-slate-500" data-testid={`job-repeats-${j.id}`}>{j.upcomingCount ? "Next visit" : "Last visit"} · repeats · {j.visitCount} visits ({j.upcomingCount} upcoming)</span>}
                  </TableCell>
                  <TableCell><Badge variant="outline" className="border-brand/30 text-brand">{jtLabel(j.jobType)}{j.jobType === "other" && j.jobTypeOther ? ` · ${j.jobTypeOther}` : ""}</Badge></TableCell>
                  <TableCell>{j.clientName}</TableCell>
                  <TableCell className="max-w-[200px] truncate"><span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3 text-slate-400" /><MapLink address={j.propertyAddress} geo={j.propertyGeo} testid={`job-address-link-${j.id}`} /></span></TableCell>
                  <TableCell>
                    {j.workers?.length ? (
                      <div className="flex flex-wrap gap-1" data-testid={`job-workers-${j.id}`}>
                        {j.workers.map((w) => <Badge key={w.id} variant="outline" className="border-slate-300 text-slate-700">{w.name}</Badge>)}
                      </div>
                    ) : <span className="text-slate-400">{t("job.unassigned", "Unassigned")}</span>}
                  </TableCell>
                  <TableCell className="font-mono text-xs">{j.startTime}–{j.endTime}</TableCell>
                  <TableCell><Badge variant="outline" className={STAT[j.status] || ""}>{t(`st.${j.status}`, j.status)}</Badge></TableCell>
                  {can("job.manage") && (
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                      <Button variant="ghost" size="sm" data-testid={`job-duplicate-${j.id}`} className="text-brand hover:bg-brand/5 h-8" onClick={() => { setDup(j); setDupDate(j.date || ""); }}>
                        <Copy className="h-4 w-4 mr-1" />{t("cal.duplicate", "Duplicate")}
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={!!dup} onOpenChange={(o) => { if (!o) { setDup(null); setDupDate(""); } }}>
        <DialogContent data-testid="job-duplicate-dialog">
          <DialogHeader><DialogTitle>{t("cal.duplicate", "Duplicate job")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-500">{t("job.duplicateHint", "Copies this job (client, property, workers, instructions) to a new date.")}</p>
            <div><Label>{t("job.newDate", "New date")}</Label>
              <Input type="date" data-testid="job-duplicate-date" value={dupDate} onChange={(e) => setDupDate(e.target.value)} className="mt-1.5" /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setDup(null); setDupDate(""); }}>{t("common.cancel", "Cancel")}</Button>
            <Button data-testid="job-duplicate-confirm" onClick={doDuplicate} disabled={busy || !dupDate} className="bg-brand hover:bg-brand-dark">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : t("cal.duplicate", "Duplicate")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
